// changelist server APIs (state GitHub has no home for).
import { api as send } from './http';
import { noteServerLimit, serverRead, type CallOptions } from './gh';

/**
 * A request to the changelist server; a rate limit its answer reports (its
 * own, or GitHub's met on the user's behalf) pauses GitHub calls too.
 */
function api<T>(...args: Parameters<typeof send>): Promise<T> {
  return send<T>(...args).catch((e: unknown) => {
    throw noteServerLimit(e);
  });
}
import { HttpError } from './cache';
import { ATTENTION_BATCH_MAX } from './limits';
import type { OperatorLinkUrls } from '../logic/operatorLinks';

export interface Me {
  id: number;
  login: string;
  name: string | null;
  avatar_url: string;
  /** One of the deployment's operators, who read feedback reports. */
  operator?: boolean;
}

export interface AppConfig {
  /** False until the GitHub App is set up (the server then serves `/setup`). Absent means true. */
  configured?: boolean;
  /** The GitHub App's slug; null until it is known. */
  app_slug: string | null;
  github_web_url: string;
  /** The server runs with CHANGELIST_DEV (localhost brief tabs allowed). */
  dev: boolean;
  /** The server offers the bring-your-own-agent features (CHANGELIST_AGENTS; on in development). Absent means off. */
  agents?: boolean;
  /** The dev-only token sign-in (`/auth/dev-token`) is available. */
  dev_token_login?: boolean;
  /** The operator's privacy, terms and support URLs (absent from older servers). */
  operator_links?: OperatorLinkUrls;
}

/** Dev-only sign-in with a personal token (a server page). */
export const DEV_TOKEN_LOGIN_PATH = '/auth/dev-token';

/** Server-rendered page that sets up the GitHub App. */
export const SETUP_PATH = '/setup';

export function isConfigured(c: AppConfig | null | undefined): boolean {
  return c?.configured !== false;
}

export interface Mark {
  block_key: string;
  reviewed: boolean;
  updated_at?: string;
}

/** A manual attention override, as the server stores it (logins lowercased). */
export interface ServerOverride {
  login: string;
  state: 'add' | 'remove';
  /** ISO time of the latest timeline event the override was made against; null when the writer sent none. */
  basis: string | null;
  /** Who wrote it; null when that user is no longer known. */
  set_by: { id: number; login: string } | null;
  set_at: string;
}

const OVERRIDE_KEYS = ['login', 'state', 'basis', 'set_by', 'set_at'] as const satisfies readonly (keyof ServerOverride)[];

export interface OrgSettings {
  allow_merge: boolean;
  brief_origins: string[];
}

export const DEFAULT_ORG_SETTINGS: OrgSettings = { allow_merge: true, brief_origins: [] };

/**
 * The fixed policy of a personal account, which has no organization policy:
 * merging allowed, briefs from any https: origin. The server's
 * `OrgSettings::personal` is the rule (the merge gate applies it); the PR
 * page uses this copy for a repository GraphQL already says a user owns,
 * without asking.
 */
export const PERSONAL_POLICY: OrgSettings = { allow_merge: true, brief_origins: [] };

/**
 * The policy that governs a pull request, as far as the client knows it.
 * Only `ready` grants anything: a policy still loading, or one that could
 * not be read, never falls back to the defaults. A repository owned by a
 * user is governed by `PERSONAL_POLICY`.
 */
export type OrgPolicy =
  | { status: 'loading' }
  | { status: 'ready'; settings: OrgSettings }
  /**
   * A policy saved before organization IDs were recorded may be this
   * organization's and is not resolved yet: no policy is in force (the
   * server refuses merges), until it is resolved or an admin saves one.
   */
  | { status: 'pending' }
  | { status: 'error'; message: string };

/** The policy as the server reports it: in force, or pending. */
export type ReadPolicy = Extract<OrgPolicy, { status: 'ready' } | { status: 'pending' }>;

/**
 * The settings page's view of an account: an organization's policy and
 * whether the viewer (an org admin) may change it, or a personal account's
 * fixed policy, which nobody can.
 */
export type OrgSettingsView =
  | (ReadPolicy & { canEdit: boolean })
  | { status: 'personal'; settings: OrgSettings; canEdit: false };

/**
 * A response body that lacks a key the contract requires: the client and
 * server disagree. It fails the request like an HTTP error would, so a
 * contract break shows up as a load error and never as defaults.
 */
export class DecodeError extends Error {}

/** `value` as a `T`, once it is checked to be an object with each of `keys`. */
function expect<T extends object>(value: unknown, keys: readonly (keyof T & string)[], what: string): T {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    throw new DecodeError(`the server sent ${what} that is not an object`);
  for (const k of keys) if (!(k in value)) throw new DecodeError(`the server sent ${what} without "${k}"`);
  return value as T;
}

/** `value` as a list of `T` (see `expect`). */
function expectList<T extends object>(value: unknown, keys: readonly (keyof T & string)[], what: string): T[] {
  if (!Array.isArray(value)) throw new DecodeError(`the server sent ${what} that is not a list`);
  return value.map((v) => expect<T>(v, keys, what));
}

const pr = (o: string, r: string, n: number) =>
  `/api/repos/${encodeURIComponent(o)}/${encodeURIComponent(r)}/pulls/${n}`;

/** The signed-in user, or null when signed out. `operator`: they read feedback reports (absent from older servers: no). */
export async function me(): Promise<Me | null> {
  try {
    const r = await api<{ user: Me; operator?: boolean }>('/api/me');
    return { ...r.user, operator: r.operator === true };
  } catch (e) {
    if (e instanceof HttpError && e.status === 401) return null;
    throw e;
  }
}

export function config(): Promise<AppConfig> {
  return api<AppConfig>('/api/config');
}

export async function getMarks(o: string, r: string, n: number): Promise<Mark[]> {
  return (await api<{ marks: Mark[] }>(pr(o, r, n) + '/marks')).marks;
}

export async function putMarks(o: string, r: string, n: number, marks: Mark[]): Promise<Mark[]> {
  return (await api<{ marks: Mark[] }>(pr(o, r, n) + '/marks', { method: 'PUT', body: { marks } })).marks;
}

/** A review mark as the marks API (version 3) names it: lines
 *  `start_line..=end_line` of the blob `blob` at `path`, read as removed
 *  (`old`) or added (`new`) lines (docs/design.md "Review marks"). */
export interface WireAnchor {
  path: string;
  side: 'old' | 'new';
  blob: string;
  start_line: number;
  end_line: number;
  /** The resolved key-based marks whose lines it holds. */
  origins?: string[];
}

/** An anchor as the server stores it, marked or unmarked. */
export type StoredAnchor = WireAnchor & { reviewed: boolean; updated_at: string };

/** The blobs a view shows at each path, by which its marks are looked up. */
export type ShownBlobs = Record<string, string[]>;

/** The marks API's answer: anchors (unmarks included). */
export interface MarksAnswer {
  version: 3;
  anchors: StoredAnchor[];
}

/** A pending key-based mark of a pull request. */
export interface LegacyKey {
  block_key: string;
  updated_at: string;
}

/** What a client found key-based marks to cover (`POST …/marks/resolve`). */
export interface MarksResolution {
  resolved: { block_key: string; anchors: WireAnchor[] }[];
  unresolvable: string[];
}

/** A flag (a thread marked reviewed, a draft set aside) as the flags API names it. */
export interface WireFlag {
  name: string;
  value: string;
  active: boolean;
}

export type StoredFlag = WireFlag & { updated_at: string };

const repoBase = (o: string, r: string) => `/api/repos/${encodeURIComponent(o)}/${encodeURIComponent(r)}`;

/** The anchors on `blobs` (`POST …/marks/lookup`). */
export function lookupMarks(o: string, r: string, blobs: ShownBlobs): Promise<MarksAnswer> {
  return api<MarksAnswer>(repoBase(o, r) + '/marks/lookup', { method: 'POST', body: { blobs } });
}

/** Write anchors; answers them as stored. */
export function putAnchors(o: string, r: string, anchors: (WireAnchor & { reviewed: boolean })[]): Promise<MarksAnswer> {
  return api<MarksAnswer>(repoBase(o, r) + '/marks', { method: 'PUT', body: { anchors } });
}

export async function getLegacy(o: string, r: string, n: number): Promise<LegacyKey[]> {
  return (await api<{ legacy: LegacyKey[] }>(pr(o, r, n) + '/marks/legacy')).legacy;
}

export function resolveMarks(o: string, r: string, n: number, body: MarksResolution): Promise<MarksAnswer & { legacy: LegacyKey[] }> {
  return api<MarksAnswer & { legacy: LegacyKey[] }>(pr(o, r, n) + '/marks/resolve', { method: 'POST', body });
}

/** The flags on a subject (`#12` for pull request 12). */
export async function getFlags(o: string, r: string, subject: string): Promise<StoredFlag[]> {
  return (await api<{ flags: StoredFlag[] }>(repoBase(o, r) + '/flags?subject=' + encodeURIComponent(subject))).flags;
}

export async function putFlags(o: string, r: string, subject: string, flags: WireFlag[]): Promise<StoredFlag[]> {
  return (await api<{ flags: StoredFlag[] }>(repoBase(o, r) + '/flags', { method: 'PUT', body: { subject, flags } })).flags;
}

export async function getAttention(o: string, r: string, n: number): Promise<ServerOverride[]> {
  const res = expect<{ overrides: unknown }>(await api<unknown>(pr(o, r, n) + '/attention'), ['overrides'], 'attention');
  return expectList<ServerOverride>(res.overrides, OVERRIDE_KEYS, 'an attention override');
}

/**
 * Write `login`'s override. A refusal for a cap (409 `too_many`, told
 * apart by its `cap`) is told in words: the viewer's own room is full
 * (`user`), the room for people outside the repository is (`outsiders`:
 * a collaborator can still make the change), or the pull request's is.
 */
export async function putAttention(o: string, r: string, n: number, login: string, state: 'add' | 'remove', basis: string) {
  try {
    return await api<unknown>(pr(o, r, n) + '/attention/' + encodeURIComponent(login), {
      method: 'PUT',
      body: { state, basis },
    });
  } catch (e) {
    const body = e instanceof HttpError && e.status === 409 ? (e.body as { error?: string; cap?: string; detail?: string } | null) : null;
    if (body?.error !== 'too_many') throw e;
    // The number is the only part of `detail` read: shown, never matched on.
    const most = /(\d+)/.exec(body.detail ?? '')?.[1];
    const cap = most ? ` (${most})` : '';
    if (body.cap === 'user')
      throw new Error(`you have already made as many attention changes as one person may${cap}; undo some of your manual changes to make room`);
    if (body.cap === 'outsiders')
      throw new Error(`people outside ${o}/${r} have already made as many attention changes on this pull request as it takes from outside${cap}; a collaborator on ${o}/${r} can still make this one`);
    throw new Error(`this pull request already holds as many attention changes as it takes${cap}; undo a manual change to make room`);
  }
}

export function clearAttention(o: string, r: string, n: number, login: string) {
  return api<unknown>(pr(o, r, n) + '/attention/' + encodeURIComponent(login), { method: 'DELETE' });
}

export interface PullRef {
  owner: string;
  repo: string;
  number: number;
}

export interface BatchOverrides {
  owner: string;
  repo: string;
  number: number;
  overrides: ServerOverride[];
}

/**
 * Overrides for many pull requests, asked for ATTENTION_BATCH_MAX per
 * request, scheduled with GitHub's calls (`serverRead`); those in
 * repositories the viewer cannot read are left out. Fails if any request
 * fails.
 */
export async function attentionBatch(pulls: PullRef[], opts: CallOptions = {}): Promise<BatchOverrides[]> {
  const chunks: PullRef[][] = [];
  for (let i = 0; i < pulls.length; i += ATTENTION_BATCH_MAX) chunks.push(pulls.slice(i, i + ATTENTION_BATCH_MAX));
  return (await Promise.all(chunks.map((c) => attentionChunk(c, opts)))).flat();
}

async function attentionChunk(pulls: PullRef[], opts: CallOptions): Promise<BatchOverrides[]> {
  const body = await serverRead(
    (signal) => send<unknown>('/api/attention/batch', { method: 'POST', body: { pulls }, signal }),
    { ...opts, key: `attention ${JSON.stringify(pulls)}` },
  );
  const res = expect<{ pulls: unknown }>(body, ['pulls'], 'an attention batch');
  return expectList<BatchOverrides>(res.pulls, ['owner', 'repo', 'number', 'overrides'], 'a batch entry').map((b) => ({
    ...b,
    overrides: expectList<ServerOverride>(b.overrides, OVERRIDE_KEYS, 'an attention override'),
  }));
}

/**
 * The policy that governs `org/repo`. Naming the repository lets anyone who
 * can read it get the policy, not only active members of the organization.
 */
export async function orgPolicy(org: string, repo: string): Promise<ReadPolicy> {
  const view = await orgSettingsAccess(org, repo);
  return view.status === 'pending' ? { status: 'pending' } : { status: 'ready', settings: view.settings };
}

/**
 * An org's policy and whether the viewer (an org admin) may change it.
 * Without `repo`, only active members of the organization may read them.
 */
export async function orgSettingsAccess(org: string, repo?: string): Promise<OrgSettingsView> {
  const query = repo === undefined ? '' : '?repo=' + encodeURIComponent(repo);
  const body = await api<unknown>(`/api/orgs/${encodeURIComponent(org)}/settings${query}`);
  const res = expect<{ org: string; status: string; settings: unknown; updated_at: string | null; can_edit: boolean }>(
    body,
    ['org', 'status', 'settings', 'updated_at', 'can_edit'],
    'org settings',
  );
  if (res.status === 'pending') return { status: 'pending', canEdit: res.can_edit };
  if (res.status !== 'ready' && res.status !== 'personal')
    throw new DecodeError(`the server sent org settings with status "${res.status}"`);
  const s = expect<OrgSettings>(res.settings, ['allow_merge', 'brief_origins'], 'an org policy');
  if (typeof s.allow_merge !== 'boolean') throw new DecodeError('the server sent an org policy whose "allow_merge" is not a boolean');
  if (!Array.isArray(s.brief_origins) || !s.brief_origins.every((o) => typeof o === 'string'))
    throw new DecodeError('the server sent an org policy whose "brief_origins" is not a list of strings');
  // Only the fields this client knows, so a save never sends back ones it does not.
  const settings = { allow_merge: s.allow_merge, brief_origins: s.brief_origins };
  return res.status === 'personal' ? { status: 'personal', settings, canEdit: false } : { status: 'ready', settings, canEdit: res.can_edit };
}

export function putOrgSettings(org: string, settings: OrgSettings) {
  return api<unknown>(`/api/orgs/${encodeURIComponent(org)}/settings`, { method: 'PUT', body: settings });
}

export type DiffSide = 'LEFT' | 'RIGHT';

/**
 * Where a review draft is published: a review thread on lines of the diff
 * (`line`), a file-level review thread (`file`), a reply in a review thread
 * (`reply`; `path` and `line` place it), or a conversation comment for a
 * line outside the diff (`conversation`).
 */
export type DraftTarget =
  | { kind: 'line'; path: string; side: DiffSide; line: number; start_line: number | null; commit_sha: string }
  | { kind: 'file'; path: string; commit_sha: string }
  | { kind: 'reply'; thread_id: string; path: string; line: number | null }
  | { kind: 'conversation'; path: string; side: DiffSide; line: number; commit_sha: string }
  /** On the whole pull request, written on head `commit_sha`: published in the review's body (logic/inbox `reviewBody`), never as a thread. */
  | { kind: 'pr'; path: ''; commit_sha: string };

/**
 * Whether a draft is included in the review being prepared. Every draft
 * starts not included (`toReview` on the wire); the user includes it
 * (`accepted`: when, and on which head of the pull request, since a later
 * head takes it out again). See logic/inbox for the words the user reads.
 */
export type DraftState = { kind: 'toReview' } | { kind: 'accepted'; at: string; head: string };

/**
 * Who wrote a draft: the user (this session) or an agent (its own
 * credential). The server sets it from the credential, never from a
 * request. Neither starts included.
 */
export type DraftAuthor = 'user' | 'agent';

/**
 * Where a draft came from: written in Changeling (`app`), or mirroring a
 * comment of the viewer's pending review on GitHub (`pendingReview`):
 * `github_id` is that comment, `edited` says its text here differs from
 * the comment's, and `on_github` is false while the comment is not on
 * GitHub (a publish could not put it back, or it was restored after a
 * discard): the draft is then kept here only.
 */
export type DraftOrigin = { kind: 'app' } | { kind: 'pendingReview'; github_id: string | null; edited: boolean; on_github: boolean };

/** A review draft, held by the changelist server until the user publishes (`/held-comments`). */
export interface ReviewDraft {
  id: number | string;
  target: DraftTarget;
  /** The full comment body as it will be published (an off-diff one with its header and anchor marker). */
  body: string;
  state: DraftState;
  author: DraftAuthor;
  origin: DraftOrigin;
  created_at: string;
  updated_at: string;
}

export interface NewDraft {
  target: DraftTarget;
  body: string;
}

/** A change to a draft: its text, or whether it is included (including names the head the client shows). */
export type DraftChange = { body: string } | { state: 'toReview' } | { state: 'accepted'; head: string };

/** A publish, exactly as the preview showed it (see `publishRequest` in logic/inbox). */
export interface PublishRequest {
  review: { event: 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES'; body: string; commit_sha: string } | null;
  inline: { id: ReviewDraft['id']; updated_at: string }[];
  conversation: { id: ReviewDraft['id']; updated_at: string }[];
  resolve: string[];
}

/** What a publish did: the review submitted or not, the drafts published, and those kept after `error`. */
export interface PublishResponse {
  reviewed: boolean;
  /** GitHub's time for the last thing the publish wrote (its review, or a later conversation comment); null when none is known. */
  written_at?: string | null;
  published: ReviewDraft['id'][];
  kept: ReviewDraft['id'][];
  error: string | null;
  /**
   * Drafts mirroring the viewer's pending review on GitHub that the publish
   * could not put back there, and why: they are kept as drafts, and the
   * next publish tries again.
   */
  pending_kept?: ReviewDraft['id'][];
  pending_error?: string | null;
}

const held = (o: string, r: string, n: number, id?: ReviewDraft['id'] | 'mirror' | 'publish') =>
  pr(o, r, n) + '/held-comments' + (id === undefined ? '' : '/' + encodeURIComponent(String(id)));

/** A pull request where the signed-in user holds drafts (`GET /api/me/drafts`). */
export interface DraftPull {
  /** `owner/name`, lowercased. */
  repo: string;
  number: number;
  /** How many live drafts it holds. */
  drafts: number;
  /** When the newest of them last changed. */
  updated_at: string;
}

/** The signed-in user's preferences on the server (named JSON values, shared by every browser and the extension) and the revision each was last written at. */
export interface Preferences {
  preferences: Record<string, unknown>;
  revisions: Record<string, unknown>;
}

const preferencesOf = (body: unknown): Preferences => {
  const b = expect<Preferences>(body, ['preferences', 'revisions'], 'the preferences');
  return { preferences: b.preferences ?? {}, revisions: b.revisions ?? {} };
};

export async function getPreferences(): Promise<Preferences> {
  return preferencesOf(await api<unknown>('/api/me/preferences'));
}

/**
 * Set the preference `name` to `value` (null removes it), keeping the
 * others; resolves to all of them as stored. With `from`, the write is
 * made from that revision of the name (0: not set), and answers
 * `conflict`, changing nothing, when the server is at another.
 */
export async function putPreference(name: string, value: unknown, from?: number): Promise<Preferences | 'conflict'> {
  try {
    return preferencesOf(
      await api<unknown>(`/api/me/preferences/${encodeURIComponent(name)}`, {
        method: 'PUT',
        body: value,
        ...(from === undefined ? {} : { headers: { 'If-Match': `"${from}"` } }),
      }),
    );
  } catch (e) {
    if (from !== undefined && e instanceof HttpError && e.status === 409) return 'conflict';
    throw e;
  }
}

/** The pull requests holding the signed-in user's drafts, most recently changed first. */
export async function myDrafts(): Promise<DraftPull[]> {
  const body = expect<{ pulls: unknown }>(await api<unknown>('/api/me/drafts'), ['pulls'], 'the drafts list');
  return expectList<DraftPull>(body.pulls, ['repo', 'number', 'drafts', 'updated_at'], 'a pull request with drafts');
}

export async function getDrafts(o: string, r: string, n: number): Promise<ReviewDraft[]> {
  return (await api<{ comments: ReviewDraft[] }>(held(o, r, n))).comments;
}

export async function createDraft(o: string, r: string, n: number, d: NewDraft): Promise<ReviewDraft> {
  try {
    return (await api<{ comment: ReviewDraft }>(held(o, r, n), { method: 'POST', body: d })).comment;
  } catch (e) {
    if (e instanceof HttpError && e.status === 409 && (e.body as { error?: string } | null)?.error === 'too_many')
      throw new Error('you have 500 drafts on this pull request; publish or discard some first');
    throw e;
  }
}

export async function updateDraft(o: string, r: string, n: number, id: ReviewDraft['id'], change: DraftChange): Promise<ReviewDraft> {
  return (await api<{ comment: ReviewDraft }>(held(o, r, n, id), { method: 'PATCH', body: change })).comment;
}

/**
 * What discarding a draft that mirrors a comment of the viewer's pending
 * review found, and did, on GitHub: the comment was still pending and was
 * `deleted` there; it was `published` since (the review was submitted), so
 * nothing was deleted there and the draft is gone for good; or it was
 * `gone` from GitHub already. Null for a draft that mirrors nothing.
 */
export type DiscardedOnGitHub = 'deleted' | 'published' | 'gone' | null;

/** Discard a draft. The server deletes a mirrored draft's comment on GitHub only while GitHub shows it still pending. */
export async function deleteDraft(o: string, r: string, n: number, id: ReviewDraft['id']): Promise<DiscardedOnGitHub> {
  try {
    const res = await api<{ github?: DiscardedOnGitHub } | null>(held(o, r, n, id), { method: 'DELETE' });
    return res?.github ?? null;
  } catch (e) {
    // Already gone (deleted on another device): that is what we wanted.
    if (e instanceof HttpError && e.status === 404) return null;
    throw e;
  }
}

/** Bring back a draft discarded in the last few minutes (Undo), as it was. */
export async function restoreDraft(o: string, r: string, n: number, id: ReviewDraft['id']): Promise<ReviewDraft> {
  return (await api<{ comment: ReviewDraft }>(held(o, r, n, id) + '/restore', { method: 'POST' })).comment;
}

/**
 * Read the viewer's pending review on GitHub and mirror it in the drafts
 * (`changed`: any draft was added, updated or removed by it). Only the
 * Changeling server's copy changes: nothing is written to GitHub.
 */
export async function mirrorDrafts(o: string, r: string, n: number): Promise<{ drafts: ReviewDraft[]; changed: boolean }> {
  const res = await api<{ comments: ReviewDraft[]; imported: number; changed?: number }>(held(o, r, n, 'mirror'), { method: 'POST' });
  return { drafts: res.comments, changed: res.imported + (res.changed ?? 0) > 0 };
}

export function publishDrafts(o: string, r: string, n: number, req: PublishRequest): Promise<PublishResponse> {
  return api<PublishResponse>(held(o, r, n, 'publish'), { method: 'POST', body: req });
}

/**
 * Who wrote a note: the user, or one of their agents (named as it is
 * shown). Either way the note is the user's own, seen by nobody else.
 */
export type NoteAuthor = { kind: 'user' } | { kind: 'agent'; label: string };

/**
 * What became of promoting a note to a draft: its draft `draft` is held on
 * pull request `pull`, or that draft was published (`at`). A note never
 * promoted, or whose draft was discarded since, has none (it can be
 * promoted again).
 */
export type NotePromotion = { kind: 'draft'; pull: number; draft: number } | { kind: 'published'; pull: number; at: string };

/**
 * A private note on content (`/notes`, docs/design.md "Notes"): lines
 * `start_line..end_line` of blob `blob` at `path`, read as removed (`old`)
 * or added (`new`) lines, written on `commit_sha`. It belongs to no pull
 * request: every view that shows the blob shows it.
 */
export interface Note {
  id: number;
  path: string;
  side: 'old' | 'new';
  blob: string;
  start_line: number;
  end_line: number;
  commit_sha: string;
  author: NoteAuthor;
  body: string;
  /** What became of promoting it; null while it can be promoted. */
  promotion: NotePromotion | null;
  /** The relay task an agent's note holds the result of; null for every other note. */
  source_task: string | null;
  created_at: string;
  updated_at: string;
}

/** A new note: where it is, and what it says. */
export type NewNote = Pick<Note, 'path' | 'side' | 'blob' | 'start_line' | 'end_line' | 'commit_sha' | 'body'> & { author?: NoteAuthor };

/** An agent's result kept as a note: by its agent, naming the relay task it answers (kept once per task). */
export type ResultNote = Omit<NewNote, 'author'> & { author: Extract<NoteAuthor, { kind: 'agent' }>; source_task: string };

/** Where a note is promoted to: a line draft on the right side, or a file draft (a note's lines outside the diff). */
export type PromotionTarget = Extract<DraftTarget, { kind: 'line' } | { kind: 'file' }>;

/** A note promoted to a draft of pull request `pull`: where the draft goes, and its text. */
export interface NewPromotion {
  pull: number;
  target: PromotionTarget;
  body: string;
}

/** Most path and blob pairs one lookup names (the server's `MAX_LOOKUP`). */
export const NOTES_PER_LOOKUP = 5000;

const notesOf = (o: string, r: string, id?: number | 'lookup') =>
  `/api/repos/${encodeURIComponent(o)}/${encodeURIComponent(r)}/notes` + (id === undefined ? '' : '/' + encodeURIComponent(String(id)));

/** The user's notes on those blobs of those paths (at most `NOTES_PER_LOOKUP` pairs). */
export async function lookupNotes(o: string, r: string, blobs: Record<string, string[]>): Promise<Note[]> {
  const body = expect<{ notes: unknown }>(await api<unknown>(notesOf(o, r, 'lookup'), { method: 'POST', body: { blobs } }), ['notes'], 'the notes');
  return expectList<Note>(body.notes, ['id', 'path', 'side', 'blob', 'start_line', 'end_line', 'author', 'body'], 'a note');
}

export async function createNote(o: string, r: string, n: NewNote): Promise<Note> {
  try {
    return (await api<{ note: Note }>(notesOf(o, r), { method: 'POST', body: n })).note;
  } catch (e) {
    if (e instanceof HttpError && e.status === 409 && (e.body as { error?: string } | null)?.error === 'too_many')
      throw new Error(`you have as many notes as you can keep${(e.body as { cap?: string }).cap === 'repo' ? ' in this repository' : ''}; delete some first`);
    throw e;
  }
}

export async function updateNote(o: string, r: string, id: number, body: string): Promise<Note> {
  return (await api<{ note: Note }>(notesOf(o, r, id), { method: 'PATCH', body: { body } })).note;
}

/** Delete a note; restorable for a few minutes (`restoreNote`). One already gone is what was wanted. */
export async function deleteNote(o: string, r: string, id: number): Promise<void> {
  try {
    await api<null>(notesOf(o, r, id), { method: 'DELETE' });
  } catch (e) {
    if (e instanceof HttpError && e.status === 404) return;
    throw e;
  }
}

/** Bring back a note deleted in the last few minutes (Undo), as it was. */
export async function restoreNote(o: string, r: string, id: number): Promise<Note> {
  return (await api<{ note: Note }>(notesOf(o, r, id) + '/restore', { method: 'POST' })).note;
}

/**
 * Keep an agent's result as a note, once per task: the note it is already
 * when one was kept before (by this tab, another, or another browser), and
 * null when that note was deleted since (the user removed it: it is not
 * made again).
 */
export async function keepResultNote(o: string, r: string, n: ResultNote): Promise<Note | null> {
  try {
    return (await api<{ note: Note }>(notesOf(o, r), { method: 'POST', body: n })).note;
  } catch (e) {
    if (e instanceof HttpError && e.status === 409 && (e.body as { error?: string } | null)?.error === 'conflict') return null;
    throw e;
  }
}

/**
 * Promote note `id` to a draft (one write: the draft is held and the note
 * names it, or neither). Refusals say why: the note is promoted already,
 * its draft was published, a publish is running, or the drafts are full.
 */
export async function promoteNote(o: string, r: string, id: number, p: NewPromotion): Promise<{ note: Note; comment: ReviewDraft }> {
  try {
    return await api<{ note: Note; comment: ReviewDraft }>(notesOf(o, r, id) + '/promote', { method: 'POST', body: p });
  } catch (e) {
    const body = e instanceof HttpError ? (e.body as { error?: string; detail?: string } | null) : null;
    if (e instanceof HttpError && e.status === 409 && body?.error === 'too_many') throw new Error('you have as many drafts as you can keep; publish or discard some first');
    if (e instanceof HttpError && e.status === 409 && body?.detail) throw new Error(body.detail);
    throw e;
  }
}
